import { useState } from 'preact/hooks';

import type { Api } from '../api';
import type { Station } from '../types';

interface Props {
  api: Api;
  onJoined: (station: Station, pin: string) => void;
}

/**
 * Anmeldung des Geräts an einer Station.
 *
 * Hier wird auch die Tonausgabe freigeschaltet: iOS gibt Audio erst nach
 * einer Nutzergeste frei, und das erste Signal, das stumm bliebe, wäre
 * ausgerechnet die erste Ablehnung.
 */
export function JoinView({ api, onJoined }: Props) {
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pinValid = /^\d{4}$/.test(pin);

  async function join(): Promise<void> {
    setError(null);
    if (!code.trim()) return setError('Bitte den Code eingeben.');
    if (!pinValid) return setError('Bitte eine vierstellige PIN für die Bildschirmsperre wählen.');

    setBusy(true);
    const outcome = await api.join(code.trim());
    setBusy(false);

    if (!outcome.ok) {
      setError(
        outcome.kind === 'network'
          ? 'Keine Verbindung zum Server.'
          : outcome.kind === 'unauthorized'
            ? 'Der Code ist ungültig oder abgelaufen.'
            : outcome.message,
      );
      return;
    }
    onJoined(outcome.data.station, pin);
  }

  return (
    <div class="screen screen--join">
      <h1>Kartenkontrolle</h1>
      {/* Der Gerätename kam bis zum 03.09.2026 von hier und war freiwillig.
          In der Geräteliste im Office stand deshalb oft "ohne Namen",
          ausgerechnet dort, wo man eines sperren muss. Jetzt vergibt ihn das
          Office am Geräteplatz, und der Code gehört zu diesem Platz. */}
      <p class="lead">Code vom Office eingeben, um dieses Gerät anzumelden.</p>

      <label class="field">
        <span>Einlöse-Code</span>
        <input
          class="code-input"
          value={code}
          onInput={(e) => setCode((e.target as HTMLInputElement).value)}
          placeholder="z. B. K7M4QP"
          autocomplete="off"
          autocapitalize="characters"
          aria-label="Einlöse-Code"
        />
      </label>

      <label class="field">
        <span>PIN für die Bildschirmsperre</span>
        <input
          type="tel"
          inputMode="numeric"
          maxLength={4}
          value={pin}
          onInput={(e) => setPin((e.target as HTMLInputElement).value.replace(/\D/g, ''))}
          placeholder="4 Ziffern"
          aria-label="PIN"
        />
      </label>

      {error && (
        <p class="error" role="alert">
          {error}
        </p>
      )}

      <button class="btn btn--primary btn--wide" onClick={join} disabled={busy}>
        {busy ? 'Melde an …' : 'Gerät anmelden'}
      </button>
    </div>
  );
}
